import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import {
  AuthenticatedSessionContext,
  useAuthenticatedSession,
} from '../../authenticatedSession/AuthenticatedSessionContext';
import {
  CommunicationEventDocument,
  EngagementLetterDocument,
  EventDocument,
  ExpenseEntryData,
  ExpenseEntryDocument,
  FinancialInstitutionDocument,
  FirmDocument,
  HumanDocument,
  InvoiceData,
  InvoiceDocument,
  KnowledgeBasePageDocument,
  MatterData,
  MatterDocument,
  MatterDocumentDocument,
  NoteData,
  NoteDocument,
  OrganizationDocument,
  TaskData,
  TaskDocument,
  TimeEntryData,
  TimeEntryDocument,
  TransactionData,
  TransactionDocument,
  UserData,
  UserDocument,
  InvoiceEmailData,
  InvoiceEmailDocument,
  InvoiceTransactionAllocationDocument,
  InvoiceTransactionAllocationData,
  MatterInvoiceBalanceEventDocument,
  MatterInvoiceBalanceEventData,
} from '../baseTypes';
import { OrderByClause, WhereClause } from '../queryBuilder';
import { EnrichedMatterContact } from '../aggregateTypes';
import { ymdIsOnOrAfter } from '../yearMonthDayUtil';
import { useLoadState } from '@/hooks/useLoadState';
import { useMatter } from './matters';

export function useFinancialInstitutions() {
  const authSession = useContext(AuthenticatedSessionContext);
  const [institutions, setInstitutions] = useState<
    FinancialInstitutionDocument[] | undefined
  >();
  useEffect(() => {
    setInstitutions(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToFinancialInstitutionsChanges(
      (snapshot) => {
        setInstitutions(
          snapshot.docs.map((doc) => {
            return { id: doc.id, ...doc.data() };
          }),
        );
      },
      [],
      [],
    );
  }, [authSession]);

  return institutions;
}

export function useKnowledgeBasePages() {
  const authSession = useContext(AuthenticatedSessionContext);
  const [pages, setPages] = useState<KnowledgeBasePageDocument[] | undefined>();
  useEffect(() => {
    setPages(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToKnowledgeBasePagesChanges(
      (snapshot) => {
        setPages(
          snapshot.docs.map((doc) => {
            return { id: doc.id, ...doc.data() };
          }),
        );
      },
      [],
      [['createdTimestamp', 'asc']],
    );
  }, [authSession]);

  return pages;
}

export function usePrimaryClientForMatter(matterId: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const matterLoadState = useMatter(matterId);
  const [primaryClient, setPrimaryClient] = useState<EnrichedMatterContact>();
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();
  const setLatestPrimaryClient = (data: EnrichedMatterContact | undefined) => {
    setPrimaryClient(data);
    setSnapshotTimestamp(new Date());
  };
  useEffect(() => {
    setLatestPrimaryClient(undefined);
    if (!authSession) {
      return;
    }
    if (matterLoadState.status !== 'loaded') {
      return;
    }
    const matter = matterLoadState.data;
    if (!matter.primaryClientId) {
      return;
    }
    const humanOrOrganization = matter.humansAndOrganizations?.find(
      (h) => h.id === matter.primaryClientId,
    );
    if (!humanOrOrganization) {
      return;
    }
    if (humanOrOrganization.type === 'human') {
      return authSession.db.subscribeToHumanChanges(
        matter.primaryClientId,
        (snapshot) => {
          setLatestPrimaryClient(undefined);
          const data = snapshot.data();
          if (!data) {
            return;
          }
          setLatestPrimaryClient({
            ...data,
            id: snapshot.id,
            role: 'client',
            type: 'human',
          });
        },
      );
    } else {
      return authSession.db.subscribeToOrganizationChanges(
        matter.primaryClientId,
        (snapshot) => {
          setLatestPrimaryClient(undefined);
          const data = snapshot.data();
          if (!data) {
            return;
          }
          setLatestPrimaryClient({
            ...data,
            id: snapshot.id,
            role: 'client',
            type: 'organization',
          });
        },
      );
    }
  }, [authSession, matterId, matterLoadState.snapshotId]);
  return { primaryClient, snapshotTimestamp };
}

export function usePrimaryAdversePartyForMatter(matterId: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const matterLoadState = useMatter(matterId);
  const [primaryAdverseParty, setPrimaryAdverseParty] =
    useState<EnrichedMatterContact>();
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();
  const setLatestPrimaryAdverseParty = (
    data: EnrichedMatterContact | undefined,
  ) => {
    setPrimaryAdverseParty(data);
    setSnapshotTimestamp(new Date());
  };
  useEffect(() => {
    setLatestPrimaryAdverseParty(undefined);
    if (!authSession) {
      return;
    }
    if (matterLoadState.status !== 'loaded') {
      return;
    }
    const matter = matterLoadState.data;
    if (!matter.primaryAdversePartyId) {
      return;
    }
    const humanOrOrganization = matter.humansAndOrganizations?.find(
      (h) => h.id === matter.primaryAdversePartyId,
    );
    if (!humanOrOrganization) {
      return;
    }
    if (humanOrOrganization.type === 'human') {
      return authSession.db.subscribeToHumanChanges(
        matter.primaryAdversePartyId,
        (snapshot) => {
          setLatestPrimaryAdverseParty(undefined);
          const data = snapshot.data();
          if (!data) {
            return;
          }
          setLatestPrimaryAdverseParty({
            ...data,
            id: snapshot.id,
            role: 'adverseParty',
            type: 'human',
          });
        },
      );
    } else {
      return authSession.db.subscribeToOrganizationChanges(
        matter.primaryAdversePartyId,
        (snapshot) => {
          setLatestPrimaryAdverseParty(undefined);
          const data = snapshot.data();
          if (!data) {
            return;
          }
          setLatestPrimaryAdverseParty({
            ...data,
            id: snapshot.id,
            role: 'adverseParty',
            type: 'organization',
          });
        },
      );
    }
  }, [authSession, matterId, matterLoadState.snapshotId]);
  return { primaryAdverseParty, snapshotTimestamp };
}

export function useMatterNotes(matterId: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [notes, setNotes] = useState<NoteDocument[]>();
  useEffect(() => {
    setNotes(undefined);
    if (!authSession) {
      return;
    }
    if (!matterId) {
      return;
    }
    return authSession.db.subscribeToMatterNotesChanges(
      matterId,
      [['createdTimestamp', 'desc']],
      (collectionSnapshot) => {
        const notes = collectionSnapshot.docs
          .map((docSnapshot) => {
            const data = docSnapshot.data();
            if (!data) {
              return null;
            }
            return { ...data, id: docSnapshot.id };
          })
          .filter((note) => !!note) as NoteDocument[];

        setNotes(notes);
      },
    );
  }, [matterId]);
  return notes;
}

/**
 * Subscribes to matter events at `firms/{firmId}/events` filtered by
 * `matterId`. Returns `undefined` until the first snapshot arrives.
 */
export function useMatterEvents(matterId: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [events, setEvents] = useState<EventDocument[]>();
  useEffect(() => {
    setEvents(undefined);
    if (!authSession) {
      return;
    }
    if (!matterId) {
      return;
    }
    return authSession.db.subscribeToMatterEventsChanges(
      matterId,
      (collectionSnapshot) => {
        setEvents(
          collectionSnapshot.docs.map((docSnapshot) => ({
            ...docSnapshot.data(),
            id: docSnapshot.id,
          })),
        );
      },
    );
  }, [matterId, authSession]);
  return events;
}

export function useMatterCommunicationEvents(matterId: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [phoneCalls, setPhoneCalls] = useState<CommunicationEventDocument[]>();
  const [textMessages, setTextMessages] =
    useState<CommunicationEventDocument[]>();

  useEffect(() => {
    setPhoneCalls(undefined);
    setTextMessages(undefined);
    if (!authSession || !matterId) {
      return;
    }
    const unsubscribePhoneCalls = onSnapshot(
      authSession.db.query.phoneCalls([['matter', '==', matterId]]),
      (collectionSnapshot) => {
        setPhoneCalls(
          sortCommunicationEvents(
            collectionSnapshot.docs.map((docSnapshot) => ({
              ...docSnapshot.data(),
              id: docSnapshot.id,
            })),
          ),
        );
      },
    );
    const unsubscribeTextMessages = onSnapshot(
      authSession.db.query.textMessages([['matter', '==', matterId]]),
      (collectionSnapshot) => {
        setTextMessages(
          sortCommunicationEvents(
            collectionSnapshot.docs.map((docSnapshot) => ({
              ...docSnapshot.data(),
              id: docSnapshot.id,
            })),
          ),
        );
      },
    );
    return () => {
      unsubscribePhoneCalls();
      unsubscribeTextMessages();
    };
  }, [authSession, matterId]);

  return {
    phoneCalls,
    textMessages,
    isLoading: phoneCalls === undefined || textMessages === undefined,
  };
}

const sortCommunicationEvents = (events: CommunicationEventDocument[]) =>
  [...events].sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());

export function useMatterNoteAdd() {
  const authSession = useContext(AuthenticatedSessionContext);
  return async (matterId: string, data: NoteData) => {
    if (!authSession) {
      return;
    }
    return await authSession.db.addMatterNote(matterId, data);
  };
}

export function useMatterNoteUpdate() {
  const authSession = useContext(AuthenticatedSessionContext);
  return async (matterId: string, noteId: string, data: Partial<NoteData>) => {
    if (!authSession) {
      return;
    }
    await authSession.db.updateMatterNote(matterId, noteId, data);
  };
}

export function useMatterDocuments(matterId: string) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [items, setItems] = useState<MatterDocumentDocument[]>();
  useEffect(() => {
    setItems(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToMatterDocumentsChanges(
      matterId,
      [['createdTimestamp', 'desc']],
      (collectionSnapshot) => {
        const documents = collectionSnapshot.docs
          .map((docSnapshot) => {
            const data = docSnapshot.data();
            if (!data) {
              return null;
            }
            return { ...data, id: docSnapshot.id };
          })
          .filter((doc) => !!doc) as MatterDocumentDocument[];

        setItems(documents);
      },
    );
  }, [matterId]);
  return items;
}

export function useUsers(
  where: WhereClause<UserData>[] = [],
  orderBy: OrderByClause<UserData>[] = [],
) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [users, setUsers] = useState<UserDocument[]>();
  useEffect(() => {
    setUsers(undefined);
    if (!authSession) {
      console.log(
        'Waiting for auth session before subscribing to users changes',
      );
      return;
    }
    return authSession.db.subscribeToUsersChanges(
      (collectionSnapshot) => {
        setUsers(
          collectionSnapshot.docs.map((docSnapshot) => ({
            ...docSnapshot.data(),
            id: docSnapshot.id,
          })),
        );
      },
      where,
      orderBy,
    );
  }, [JSON.stringify(where), JSON.stringify(orderBy), authSession]);
  return users;
}

export function useUser(id: string | undefined) {
  const authSession = useContext(AuthenticatedSessionContext);
  const [user, setUser] = useState<UserDocument | undefined>();
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();
  const setLatestUser = (data: UserDocument | undefined) => {
    setUser(data);
    setSnapshotTimestamp(new Date());
  };
  useEffect(() => {
    setLatestUser(undefined);
    if (!authSession) {
      return;
    }
    if (!id) {
      return;
    }
    return authSession.db.subscribeToUserChanges(id, (snapshot) => {
      setLatestUser({ ...snapshot.data(), id: snapshot.id } as UserDocument);
    });
  }, [id, authSession]);
  return { user, snapshotTimestamp };
}

export function useCurrentUser() {
  const authSession = useContext(AuthenticatedSessionContext);
  const { user, snapshotTimestamp } = useUser(authSession?.userIds.firestore);
  return { currentUser: user, snapshotTimestamp };
}

export function useFirm() {
  const authSession = useContext(AuthenticatedSessionContext);
  const [firm, setFirm] = useState<FirmDocument | undefined>();
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();
  const setLatestFirm = (data: FirmDocument | undefined) => {
    setFirm(data);
    setSnapshotTimestamp(new Date());
  };
  useEffect(() => {
    setLatestFirm(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToFirmChanges((snapshot) => {
      setLatestFirm({ ...snapshot.data(), id: snapshot.id } as FirmDocument);
    });
  }, [authSession]);

  return { firm, snapshotTimestamp };
}

export function useInvoiceAllocationCoordinatorSnapshotTimestamp() {
  const authSession = useContext(AuthenticatedSessionContext);
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();
  useEffect(() => {
    setSnapshotTimestamp(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToInvoiceAllocationCoordinatorChanges(() => {
      setSnapshotTimestamp(new Date());
    });
  }, [authSession]);

  return snapshotTimestamp;
}

export function useTimeEntries(props: {
  where?: WhereClause<TimeEntryData>[];
}) {
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<TimeEntryDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToTimeEntriesChanges(
      props.where ?? [],
      [],
      (collectionSnapshot) => {
        setLoadState({
          status: 'loaded',
          data: collectionSnapshot.docs
            .map((docSnapshot) => ({
              ...docSnapshot.data(),
              id: docSnapshot.id,
            }))
            .sort((a, b) =>
              ymdIsOnOrAfter({ ymd: a.earnedYmd, other: b.earnedYmd }) ? -1 : 1,
            ),
        });
      },
    );
  }, [JSON.stringify(props), authSession]);

  return loadState;
}

export const useInvoices = (
  props: { where: WhereClause<InvoiceData>[] } = { where: [] },
) => {
  const { where } = props;
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<InvoiceDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    const unsubscribe = authSession.db.subscribeToInvoicesChanges(
      where,
      (snapshot) => {
        setLoadState({
          status: 'loaded',
          data: snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        });
      },
    );
    return () => {
      unsubscribe();
    };
  }, [authSession, JSON.stringify(where)]);
  return loadState;
};

export const useInvoiceTransactionAllocations = (
  props: { where: WhereClause<InvoiceTransactionAllocationData>[] } = {
    where: [],
  },
) => {
  const { where } = props;
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<
    InvoiceTransactionAllocationDocument[]
  >({ status: 'loading' });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) return;
    return authSession.db.subscribeToInvoiceTransactionAllocationsChanges(
      where,
      (snapshot) => {
        setLoadState({
          status: 'loaded',
          data: snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        });
      },
      (error) => setLoadState({ status: 'error', error }),
    );
  }, [JSON.stringify(where), authSession]);
  return loadState;
};

export const useMatterInvoiceBalanceEvents = (matterId: string) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<
    MatterInvoiceBalanceEventDocument[]
  >({ status: 'loading' });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession || !matterId) return;
    return authSession.db.subscribeToMatterInvoiceBalanceEventsChanges(
      [['matterId', '==', matterId]],
      (snapshot) => {
        setLoadState({
          status: 'loaded',
          data: snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        });
      },
      (error) => setLoadState({ status: 'error', error }),
    );
  }, [authSession, matterId]);
  return loadState;
};

export const useInvoice = (id: string) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<InvoiceDocument>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    const unsubscribe = authSession.db.subscribeToInvoiceChanges(
      id,
      (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          setLoadState({
            status: 'error',
            error: new Error('Invoice not found'),
          });
          return;
        }
        setLoadState({
          status: 'loaded',
          data: { ...data, id: snapshot.id },
        });
      },
    );
    return () => {
      unsubscribe();
    };
  }, [authSession, id]);
  return loadState;
};

export const useTransactions = (
  props: { where: WhereClause<TransactionData>[] } = { where: [] },
) => {
  const { where } = props;
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<TransactionDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({
      status: 'loading',
    });
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToTransactionsChanges(where, (snapshot) => {
      setLoadState({
        status: 'loaded',
        data: snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
      });
    });
  }, [JSON.stringify(where), authSession]);
  return loadState;
};

export function useTimeEntryAdd() {
  const authSession = useContext(AuthenticatedSessionContext);
  return useCallback(async (data: TimeEntryData) => {
    if (!authSession) {
      return;
    }
    return await authSession.db.addTimeEntry(data);
  }, []);
}

export function useTimeEntry(id: string | null): TimeEntryDocument | undefined {
  const authSession = useContext(AuthenticatedSessionContext);
  const [doc, setDoc] = useState<TimeEntryDocument | undefined>(undefined);

  useEffect(() => {
    if (!authSession || !id) {
      setDoc(undefined);
      return;
    }
    return authSession.db.subscribeToTimeEntryChanges(id, setDoc);
  }, [authSession, id]);

  return doc;
}

export const useTransaction = (id: string) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const { loadState, setLoadState } = useLoadState<TransactionDocument>({
    status: 'loading',
  });
  useEffect(() => {
    if (!authSession) {
      return;
    }
    setLoadState({ status: 'loading' });
    return authSession.db.subscribeToTransactionChanges(id, (snapshot) => {
      const data = snapshot.data();
      if (!data) {
        setLoadState({
          status: 'error',
          error: new Error('Transaction not found'),
        });
        return;
      }
      setLoadState({
        status: 'loaded',
        data: { ...data, id: snapshot.id },
      });
    });
  }, [authSession, id]);
  return loadState;
};

export const useContacts = () => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [humans, setHumans] = useState<HumanDocument[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationDocument[]>(
    [],
  );
  const [matters, setMatters] = useState<MatterDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authSession) return;

    const unsubscribeHumans = authSession.db.subscribeToHumansChanges(
      (snapshot) => {
        setHumans(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      },
    );

    const unsubscribeOrganizations =
      authSession.db.subscribeToOrganizationsChanges((snapshot) => {
        setOrganizations(
          snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        );
      });

    const unsubscribeMatters = authSession.db.subscribeToMattersChanges(
      (snapshot) => {
        setMatters(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      },
    );

    setIsLoading(false);

    return () => {
      unsubscribeHumans();
      unsubscribeOrganizations();
      unsubscribeMatters();
    };
  }, [authSession]);

  const humansWithMatters = useMemo(() => {
    return humans.map((human) => ({
      ...human,
      matters: matters.filter((matter) =>
        matter.humansAndOrganizations?.some(
          (humanOrOrganization) => humanOrOrganization.id === human.id,
        ),
      ),
    }));
  }, [humans, matters]);

  const organizationsWithMatters = useMemo(() => {
    return organizations.map((organization) => ({
      ...organization,
      matters: matters.filter((matter) =>
        matter.humansAndOrganizations?.some(
          (humanOrOrganization) => humanOrOrganization.id === organization.id,
        ),
      ),
    }));
  }, [organizations, matters]);

  return { humansWithMatters, organizationsWithMatters, isLoading };
};

export function useInvoiceEmails(props: {
  where?: WhereClause<InvoiceEmailData>[];
  orderBy?: OrderByClause<InvoiceEmailData>;
}) {
  const { where, orderBy } = props;
  const authSession = useContext(AuthenticatedSessionContext);
  const [emails, setEmails] = useState<InvoiceEmailDocument[]>();
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<Date>();

  useEffect(() => {
    setEmails(undefined);
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToInvoiceEmailsChanges(
      (collectionSnapshot) => {
        const items = collectionSnapshot.docs.map((docSnapshot) => {
          return { ...docSnapshot.data(), id: docSnapshot.id };
        });
        setEmails(items);
        setSnapshotTimestamp(new Date());
      },
      where ?? [],
      orderBy ? [orderBy] : [],
    );
  }, [JSON.stringify(where), JSON.stringify(orderBy), authSession]);

  return { invoiceEmails: emails, snapshotTimestamp };
}
